import { PICKUP_MANAGEMENT_MODES, FORM_VALIDATION_MSG } from "./constants";
import { convertDateTimeToTimeString } from '@dataGrid/gridUtilities';
import { format, addDays } from "date-fns";
import { getTerminalDateByOffset } from "../../../utils/utils";
import { getFilteredShipmentsData } from "./utils";
import { isNonEmptyShipmentObj } from "./ShipmentInfo/utils";

const PICKUP_MANAGEMENT_CONFIG = {
  [PICKUP_MANAGEMENT_MODES.CREATE_PICKUP]: {
    apiName: "CreatePickup",
    getRequestParams: ({ pickupData }) => {
      const { pickupInfo: formData, pickupTerminal, userProfile } = pickupData;
      return {
        pickupTerminal,
        shipper: {
          ...formData.shipperInfo,
          shipperContactPhone:
            formData.shipperInfo.shipperContactPhone.toString(),
          shipperSpecialInstructions : undefined
        },
        shipmentData: formData.shipmentInfo
          .filter(isNonEmptyShipmentObj)
          .map((shipment, index) => ({
            ...shipment,
            sequenceNumber: (index+1).toString(),
            handlingUnits : shipment.handlingUnits || 0,
            pieces : shipment.pieces || 0
          })),
        ...getPickupDateTime(formData.pickupDateTimeInfo),
        ...formData.billingInfo,
        specialInstructions: formData.specialInstructions,
        isSwapPickup: formData.isSwapPickup,
        isDockDropPickup: formData.isDockDropPickup,
        isExhibitPickup: formData.isExhibitPickup,
        terminalCode: userProfile.terminal.code,
        isLiftGate : formData.isLiftGate
      };
    },
    getPickupDateError: (value, terminalOffset) => {
      if (!value) {
        return FORM_VALIDATION_MSG.REQUIRED;
      }
      if (
        new Date(value) <
        new Date(getTerminalDateByOffset(new Date(), terminalOffset))
      ) {
        return FORM_VALIDATION_MSG.MIN_PICKUP_DATE;
      }
      return null;
    }
  },
  [PICKUP_MANAGEMENT_MODES.EDIT_PICKUP]: {
    apiName: "EditPickup",
    getRequestParams: ({ pickupData }) => {
      const {
        pickupInfo: formData,
        pickupTerminal,
        pickupId,
        pickupNumber,
        isDateTimeUpdated,
        userProfile
      } = pickupData;
      return {
        shipper: {
          ...formData.shipperInfo,
          shipperContactPhone:
            formData.shipperInfo.shipperContactPhone.toString(),
          shipperSpecialInstructions : undefined
        },
        shipmentData: getFilteredShipmentsData(formData.shipmentInfo),
        ...getPickupDateTime(formData.pickupDateTimeInfo),
        ...formData.billingInfo,
        specialInstructions: formData.specialInstructions,
        isSwapPickup: formData.isSwapPickup,
        isDockDropPickup: formData.isDockDropPickup,
        isExhibitPickup: formData.isExhibitPickup,
        terminalCode: userProfile.terminal.code,
        editReasonInfo: isDateTimeUpdated
          ? {
              ...formData.editReasonInfo,
              ...(Boolean(formData.editReasonInfo.editReasonCode) && {
                editReasonCode: formData.editReasonInfo.editReasonCode,
                callTime: convertDateTimeToTimeString(
                  formData.editReasonInfo.callTime
                ),
              }),
            }
          : null,
        pickupId,
        pickupNumber,
        pickupTerminal,
        isLiftGate : formData.isLiftGate
      };
    },
    getPickupDateError: (value, terminalOffset) => {
      if (!value) {
        return FORM_VALIDATION_MSG.REQUIRED;
      }
      if (
        new Date(value) <
        new Date(getTerminalDateByOffset(new Date(), terminalOffset))
      ) {
        return FORM_VALIDATION_MSG.MIN_PICKUP_DATE;
      } else if (
        (new Date(value) >
          addDays(new Date(getTerminalDateByOffset(new Date(), terminalOffset)), 7))
      ) {
        return FORM_VALIDATION_MSG.MAX_PICKUP_DATE;
      }
      return null;
    }
  },
  [PICKUP_MANAGEMENT_MODES.MERGE_PICKUP] : {
    apiName: "MergePickup",
    getRequestParams: ({pickupData, selectedPickups, selectedDriver, isExhibitPickupInMergeMode, allPickups}) => {
      const { pickupInfo: formData, pickupTerminal, userProfile } = pickupData;
      return {
        shipper: {
          ...formData.shipperInfo,
          shipperContactPhone:
            formData.shipperInfo.shipperContactPhone.toString(),
          isAccountNumberDisabled: undefined,
          shipperSpecialInstructions : undefined
        },
        shipmentData: getFilteredShipmentsData(formData.shipmentInfo),
        ...getPickupDateTime(formData.pickupDateTimeInfo),
        ...formData.billingInfo,
        specialInstructions: formData.specialInstructions,
        isSwapPickup: formData.isSwapPickup,
        terminalCode: userProfile.terminal.code,
        pickupDriverMapping: selectedPickups.map(({ pickupId, driverId, groupId }) => ({
          pickupId,
          driverId,
          groupId
        })),
        pickupNumbers: [...new Set(selectedPickups.flatMap(({pickupNumberIdList }) => pickupNumberIdList.map(({ pickupNumber }) => pickupNumber)))],
        driverIdToAssignPickup: selectedDriver,
        pickupTerminal,
        isExhibitPickup: isExhibitPickupInMergeMode,
        retainedPickupId: formData.pickupId,
        isLiftGate : formData.isLiftGate
      };
    },
    getPickupDateError: (value, terminalOffset) => {
      if (!value) {
        return FORM_VALIDATION_MSG.REQUIRED;
      }
      if (
        new Date(value) <
        new Date(getTerminalDateByOffset(new Date(), terminalOffset))
      ) {
        return FORM_VALIDATION_MSG.MIN_PICKUP_DATE;
      } else if (
        new Date(value) >
        addDays(
          new Date(getTerminalDateByOffset(new Date(), terminalOffset)),
          7
        )
      ) {
        return FORM_VALIDATION_MSG.MAX_PICKUP_DATE;
      }
      return null;
    }
  }
};

const getPickupDateTime = ({
  readyTime,
  closeTime,
  pickupDate,
  route,
  dispatchZone,
}) => {
  return {
    readyTime: convertDateTimeToTimeString(readyTime),
    closeTime: convertDateTimeToTimeString(closeTime),
    pickupDate: format(pickupDate, "yyyy-MM-dd"),
    route,
    dispatchZone: dispatchZone ? dispatchZone : null,
  };
};

const getAllPickupsNumbersForMergePickup = (selectedPickups, allPickups) => {
  // Fixme: Remove this iteration logic once we have grouped pickups data in the store
  const groupIds = new Set(selectedPickups.map((pickup) => pickup.groupId));
  const pickupNumbers = [];
  allPickups.forEach((pickup) => {
    if (groupIds.has(pickup.groupId)) {
      pickupNumbers.push(pickup.pickupNumber);
    }
  });
  return pickupNumbers;
};

export { PICKUP_MANAGEMENT_CONFIG, getPickupDateTime };
